/**
 * Songs Library — list every YouTube song the user has submitted.
 *
 * Why this page exists
 * --------------------
 * Before this page, the Song Workspace only showed the SINGLE song
 * currently being processed. The user had no way to:
 *   - see the full history of submitted songs
 *   - tell which ones used the new Whisper transcription vs the old stub
 *   - retry a row whose lyrics came back as the `ॐ नमः` placeholder
 *     (e.g. the song was claimed by a phantom daemon before Whisper
 *      was wired in)
 *   - delete junk rows from failed test runs
 *
 * The data has always been on the backend — every queue row lives in
 * youtube_queue forever. This page is purely a UI surface over it,
 * powered by three new endpoints we shipped alongside:
 *
 *   GET    /youtube-queue/list?user_email=…   list (newest first)
 *   POST   /youtube-queue/{id}/retry          clone the row as pending
 *   DELETE /youtube-queue/{id}                hard delete
 *
 * Reachable from the AppShell sidebar (Library → Songs). Clicking a
 * row opens the existing ArtifactPanel inline so the user can review
 * audio, lyrics, translation, and the practice sheet without leaving.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Loader2, RefreshCw, Trash2, Music2, ExternalLink, CheckCircle2,
  AlertCircle, Clock, PlayCircle, Sparkles, Search, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSession } from "@/hooks/use-session";

export const Route = createFileRoute("/_app/library_/songs")({
  head: () => ({
    meta: [
      { title: "Songs — Learning Library" },
      { name: "description", content: "Every song you've submitted, with status and artifacts." },
    ],
  }),
  component: SongsLibrary,
});

// ---------- API shape ----------
// Mirrors backend's YoutubeQueueRead (see backend/ojaswati_backend/routers/youtube_queue.py).
type QueueRow = {
  id: number;
  url: string;
  status: "pending" | "claimed" | "uploaded" | "done" | "failed";
  worker_id: string | null;
  mp3_relpath: string | null;
  ingest_job_id: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  claimed_at: string | null;
  uploaded_at: string | null;
  request_payload: {
    url?: string;
    meta?: {
      title?: string;
      language?: string;
      language_iso?: string;
      composer?: string;
      raga?: string;
      album?: string;
      year?: string;
    };
    drive_folder_id?: string | null;
    drive_folder_name?: string | null;
  } | null;
};

// #237 — Centralised origin-aware FastAPI URL resolver. Was a local
// copy that defaulted to http://localhost:8000 on a fresh browser
// profile in production → Safari mixed-content blocked the fetch and
// the Songs page showed "Load failed". The shared helper falls back
// to the Fly URL automatically when not on localhost.
import { apiHeaders, fastapiBase } from "@/lib/fastapi-url";

function apiBase(): string {
  return fastapiBase();
}

// ---------- root component ----------
function SongsLibrary() {
  const session = useSession();
  const email = session.data?.email ?? null;
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  // Auto-refresh while there are non-terminal rows so the user sees
  // status flip from pending → claimed → uploaded → done live.
  const hasInFlight = useMemo(
    () => rows?.some((r) => r.status !== "done" && r.status !== "failed") ?? false,
    [rows],
  );

  const refresh = async () => {
    if (!email) return;
    try {
      const res = await fetch(
        `${apiBase()}/youtube-queue/list?user_email=${encodeURIComponent(email)}`,
        { headers: apiHeaders({ Accept: "application/json" }) },
      );
      if (!res.ok) {
        // #240 — Surface the proxy's `detail` field so 503 (missing
        // Cloudflare secret) tells the user EXACTLY what to do instead
        // of opaque "HTTP 503". Same shape applies to any /api/fly
        // error: { error: <code>, detail: <human-readable> }.
        let detail = `HTTP ${res.status} ${res.statusText}`;
        try {
          const body = (await res.json()) as { error?: string; detail?: string };
          if (body?.detail) detail = `${detail} — ${body.detail}`;
          else if (body?.error) detail = `${detail} — ${body.error}`;
        } catch { /* not JSON; keep the status-line detail */ }
        throw new Error(detail);
      }
      const data = (await res.json()) as QueueRow[];
      setRows(data);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load");
    }
  };

  useEffect(() => { void refresh(); /* eslint-disable-next-line */ }, [email]);
  useEffect(() => {
    if (!hasInFlight) return;
    const t = setInterval(() => { void refresh(); }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInFlight, email]);

  const onRetry = async (id: number) => {
    try {
      const res = await fetch(`${apiBase()}/youtube-queue/${id}/retry`, {
        method: "POST",
        headers: apiHeaders({ Accept: "application/json" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Re-queued — Mac daemon will pick this up within ~10s");
      void refresh();
    } catch (e: any) {
      toast.error("Retry failed", { description: e?.message });
    }
  };

  const onDelete = async (id: number) => {
    if (!confirm(`Delete song queue row #${id}? Original artifacts on Drive (if any) are not affected.`)) return;
    try {
      const res = await fetch(`${apiBase()}/youtube-queue/${id}`, {
        method: "DELETE",
        headers: apiHeaders(),
      });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      toast.success(`Deleted row #${id}`);
      void refresh();
    } catch (e: any) {
      toast.error("Delete failed", { description: e?.message });
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const m = r.request_payload?.meta ?? {};
      return (
        (m.title ?? "").toLowerCase().includes(q) ||
        (m.composer ?? "").toLowerCase().includes(q) ||
        (m.raga ?? "").toLowerCase().includes(q) ||
        r.url.toLowerCase().includes(q) ||
        r.status.includes(q)
      );
    });
  }, [rows, filter]);

  // ---- empty / loading / error states ----
  if (!email) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Sign in to see your song history.
      </div>
    );
  }
  if (rows === null && !error) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your songs…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <div className="font-medium text-destructive mb-1">Couldn't load songs</div>
          <div className="text-destructive/80 font-mono text-xs">{error}</div>
          <Button variant="outline" size="sm" onClick={refresh} className="mt-3 gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* #229 — Compact header (page title only). The action CTA moves
          to the hero tile below — matching iHub Home's pattern where
          big bordered tiles sit at the top of the page and act as the
          obvious entry into a flow. */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl flex items-center gap-2">
            <Music2 className="h-5 w-5 text-primary" /> Songs
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {rows!.length} song{rows!.length === 1 ? "" : "s"} in your library
            {hasInFlight && (
              <>
                {" "}
                · <span className="text-amber-600">auto-refreshing</span>
              </>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          className="gap-1.5 shrink-0"
          title="Refresh now"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* #229 — Hero "Add a song" tile, styled to match iHub Home's
          action tiles (rounded-2xl, border, hover lift). User feedback:
          "like home page i should be able to transition". Same visual
          grammar means she gets the same muscle memory across the app. */}
      <Link
        to="/library/song-workspace"
        className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-5 sm:p-6 mb-4 hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition"
      >
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition shrink-0">
          <Plus className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">Add a song</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Paste a YouTube URL — we'll download, transcribe, translate
            and file it to Google Drive in the background.
          </div>
        </div>
        <span className="text-primary text-sm font-medium shrink-0 group-hover:translate-x-0.5 transition">
          →
        </span>
      </Link>

      {/* Filter */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by title, composer, raga, status…"
          className="pl-9 h-9"
        />
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {rows!.length === 0 ? (
            <div className="flex flex-col items-center gap-3">
              <Music2 className="h-8 w-8 text-muted-foreground/40" />
              <div>No songs yet. Tap "New song" above to paste a YouTube URL.</div>
              <Button asChild size="sm" className="gap-1.5">
                <Link to="/library/song-workspace">
                  <Plus className="h-4 w-4" /> New song
                </Link>
              </Button>
            </div>
          ) : (
            <>No songs match "<span className="font-mono">{filter}</span>".</>
          )}
        </div>
      )}

      {/* Rows */}
      <div className="space-y-2">
        {filtered.map((row) => (
          <SongRow key={row.id} row={row} onRetry={onRetry} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

// ---------- single row ----------
function SongRow({
  row, onRetry, onDelete,
}: {
  row: QueueRow;
  onRetry: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  const meta = row.request_payload?.meta ?? {};
  const title = meta.title?.trim() || row.url;
  const sub = [meta.composer, meta.raga, meta.album, meta.year].filter(Boolean).join(" · ");
  const language = meta.language || (meta.language_iso ? meta.language_iso.toUpperCase() : null);
  const hasWhisper = !!meta.language_iso;
  const driveFolder = row.request_payload?.drive_folder_name;

  // #245 — Make the entire row clickable when the song is done.
  // User: "where is the lyrics and translation. where i can play
  // the song. the next step is not intutive". The small "open" link
  // wasn't reading as the primary affordance. Now the title doubles
  // as the play link, AND a prominent "▶ Play & view lyrics" button
  // sits on the right where the eye lands first.
  const playable = row.ingest_job_id && row.status === "done";
  // Wrap the title in a router Link when playable so a card-wide
  // click target lights up. Header + meta stay readable as plain
  // text; only the title carries the click semantics so users can
  // still select the row's text if they want.
  // #248 — Use a plain <a href> instead of TanStack <Link> for the
  // same reason as the Play & view button below: the click on Safari
  // was silently doing nothing through TanStack's intercepted onClick.
  // A regular anchor always works (TanStack still SPA-navigates when it
  // can, falls back to full page load when it can't).
  const titleEl = playable && row.ingest_job_id ? (
    <a
      href={`/library/songs/${encodeURIComponent(row.ingest_job_id)}`}
      className="font-medium truncate max-w-[36ch] text-primary hover:underline"
      title="Play & view lyrics"
    >
      {title}
    </a>
  ) : (
    <span className="font-medium truncate max-w-[36ch]" title={title}>{title}</span>
  );
  return (
    <div className={`rounded-lg border bg-card p-3 sm:p-4 transition ${playable ? "border-primary/30 hover:border-primary/60 hover:shadow-sm cursor-pointer" : "border-border hover:border-primary/30"}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {/* Status icon */}
        <StatusIcon status={row.status} />

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            {titleEl}
            <span className="text-[10px] text-muted-foreground font-mono">#{row.id}</span>
          </div>
          {sub && <div className="text-xs text-muted-foreground truncate">{sub}</div>}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <StatusBadge status={row.status} />
            {language && <Chip>{language}</Chip>}
            {/* #264a — Removed the "Whisper" chip. After #263 the
                workspace shows canonical lyrics (via Gemini grounded)
                as the primary view, with whisper only as fallback for
                unrecognised songs. Showing "Whisper" everywhere was
                misleading — the user sees canonical, not whisper.
                Stub lyrics warning kept (still useful as a "didn't
                actually transcribe" signal). */}
            {!hasWhisper && <Chip tone="warn">stub lyrics</Chip>}
            {driveFolder && <Chip>📁 {driveFolder}</Chip>}
            {row.worker_id && (
              <span className="text-[10px] text-muted-foreground font-mono" title={`Worker that processed this row: ${row.worker_id}`}>
                {row.worker_id.slice(-6)}
              </span>
            )}
          </div>
          {row.error && (
            <div className="text-[11px] text-destructive mt-1.5 font-mono truncate" title={row.error}>
              ⚠ {row.error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 ml-auto">
          {/* #245 — Replaced the tiny "open" text link with a real
              primary button so the next step is immediately obvious.
              #248 — User reported "Play & view does not work" on Safari
              even with the TanStack <Link> wired correctly + the route
              registered + the deployed bundle current. After exhausting
              SW / cache / chunk-load theories, the pragmatic fix is to
              bypass TanStack's click handler entirely and use a plain
              <a href> with the resolved URL. The TanStack router still
              picks this up via popstate/link-interception, but if for
              ANY reason its onClick is being swallowed (Safari quirks,
              event bubbling, third-party extensions), the href fallback
              guarantees the navigation happens. */}
          {playable && row.ingest_job_id && (
            <a
              href={`/library/songs/${encodeURIComponent(row.ingest_job_id)}`}
              // #248c — Same-tab navigation now works. The "goes and
              // comes back" bug was actually a routing structure bug:
              // _app.library_.songs.$jobId.tsx (dot separator) made
              // TanStack treat the detail route as a CHILD of the
              // songs LIST route, and the list has no <Outlet />, so
              // navigating to /library/songs/<id> silently rendered
              // the list page. The rename to songs_.$jobId.tsx
              // (trailing underscore) lifts the detail route to be a
              // sibling, which is what we wanted all along.
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:bg-primary/90 transition shadow-sm"
              title={`Play audio + view lyrics & translation (job ${row.ingest_job_id.slice(0, 8)}…)`}
            >
              <PlayCircle className="h-4 w-4" /> Play &amp; view
            </a>
          )}
          <a
            href={row.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground px-2 py-1"
            title="Open YouTube source"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
          <button
            type="button"
            onClick={() => onRetry(row.id)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary px-2 py-1"
            title="Re-queue this song (e.g. to re-run with Whisper)"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(row.id)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive px-2 py-1"
            title="Delete this row"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- visual helpers ----------
function StatusIcon({ status }: { status: QueueRow["status"] }) {
  switch (status) {
    case "done":     return <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />;
    case "failed":   return <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />;
    case "pending":
    case "claimed":
    case "uploaded": return <Loader2 className="h-4 w-4 text-amber-500 shrink-0 mt-0.5 animate-spin" />;
  }
}

function StatusBadge({ status }: { status: QueueRow["status"] }) {
  const map: Record<QueueRow["status"], { label: string; tone: "ok" | "warn" | "bad" | "neutral" }> = {
    pending:  { label: "queued",         tone: "warn" },
    claimed:  { label: "Mac downloading", tone: "warn" },
    uploaded: { label: "Fly processing", tone: "warn" },
    done:     { label: "done",            tone: "ok" },
    failed:   { label: "failed",          tone: "bad" },
  };
  const { label, tone } = map[status];
  return <Chip tone={tone}><Clock className="h-2.5 w-2.5 inline -mt-0.5" /> {label}</Chip>;
}

function Chip({
  children, tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "ok" | "warn" | "bad" | "neutral";
}) {
  const cls = {
    ok:      "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    warn:    "bg-amber-500/10 text-amber-700 border-amber-500/30",
    bad:     "bg-destructive/10 text-destructive border-destructive/30",
    neutral: "bg-muted/30 text-muted-foreground border-border",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
      {children}
    </span>
  );
}
